import { Observable, take } from "rxjs";
import {
  SharedModule,
  Dialog,
  DataService,
  OrderDetailFragment,
  OrderDetailQueryDocument,
  CurrencyCode,
  NotificationService,
  ModalService,
} from "@bb-vendure/admin-ui/core";
import { marker as _ } from "@biesbjerg/ngx-translate-extract-marker";
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
} from "@angular/core";
import TextTileComponent from "./components/tile.component";
import {
  FetchCustomerFromOrderIdDocument,
  FetchOrderNumberDocument,
  SetRefundRequestStatusDocument,
} from "../generated-graphql/graphql";
import OrderDetailTableComponent from "./components/order-detail-table/order-detail-table.component";
import { CustomCustomerLabelComponent } from "../custom-customer-label/custom-customer-label.component";

@Component({
  selector: "modal-detail",
  standalone: true,
  imports: [
    SharedModule,
    OrderDetailTableComponent,
    CustomCustomerLabelComponent,
  ],
  templateUrl: "./modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class ModalRefundDetailComponent implements Dialog<any>, OnInit {
  constructor(
    protected dataService: DataService,
    private notiService: NotificationService
  ) {}

  currencyCode = CurrencyCode.KRW;

  ngOnInit(): void {
    this.orderNumber = this.dataService
      .query(FetchOrderNumberDocument, {
        orderId: this.refundReq.orderId,
      })
      .mapStream((data) => data.order?.code);

    this.customer$ = this.dataService
      .query(FetchCustomerFromOrderIdDocument, {
        orderId: this.refundReq.orderId,
      })
      .mapStream((data) => data.order?.customer);

    this.buttons = [
      {
        label: "common.close",
        type: "secondary",
        returnValue: {
          id: this.refundReq.id,
          status: "CLOSE",
        },
      },
    ];

    switch (this.refundReq.status) {
      case "new":
        this.buttons.push(
          ...[
            {
              label: "common.reject",
              type: "danger",
              returnValue: {
                id: this.refundReq.id,
                status: "REJECTED",
              },
            },
            {
              label: "common.approve",
              type: "primary",
              returnValue: {
                id: this.refundReq.id,
                status: "APPROVED",
              },
            },
          ]
        );
        break;
      case "rejected":
      case "refunded":
        break;
      case "approved":
        this.buttons.push({
          label: "common.loadingOrder",
          type: "primary",
          disabled: true,
        });
        this.dataService
          .query(OrderDetailQueryDocument, {
            id: this.refundReq.orderId,
          })
          .mapStream((data) => data.order)
          .subscribe((order) => {
            this.buttons.pop();
            this.buttons.push({
              label: "common.refund-approved",
              type: "primary",
              returnValue: {
                id: this.refundReq.id,
                status: "REFUND_ORDER",
                order: order,
              },
            });
          });
        break;
    }
  }

  orderNumber: Observable<string | undefined>;
  resolveWith: (result?: {
    id: string;
    status: string;
    order: OrderDetailFragment | undefined;
  }) => void;
  buttons;
  @Input() refundReq: any;
  customer$: Observable<any>;

  close() {
    this.resolveWith();
  }

  handleApprovedButtonClick(button: any) {
    // Check if it's the close button (secondary type) or refund button (primary type)
    if (button.type === "secondary" || button.label === "common.close") {
      // Handle close button - just close the modal
      this.resolveWith(button.returnValue);
    } else if (
      button.label === "common.refund-approved" ||
      button.type === "primary"
    ) {
      // Handle refund button - initiate refund process
      this.initRefundOrder();
    } else {
      // Fallback for other buttons
      this.resolveWith(button.returnValue);
    }
  }

  initRefundOrder() {
    this.buttons.pop();
    this.buttons.push({
      label: "common.loadingOrder",
      type: "primary",
      disabled: true,
    });
    this.refundOrder();
    this.resolveWith();
  }

  async refundOrder() {
    const { args } = this.refundReq;

    try {
      await this.dataService.order.refundOrder(args.refunds[0]).subscribe({
        next: ({ refundOrder }) => {
          if (refundOrder) {
            switch (refundOrder.__typename) {
              case "Refund":
                if (refundOrder.state === "Failed") {
                  this.notiService.error(_("order.refund-order-failed"));
                } else {
                  this.notiService.success(_("order.refund-order-success"));
                  this.setStatus(this.refundReq.id, "refunded");

                  if (args.cancel && args.cancel.lines?.length) {
                    this.dataService.order.cancelOrder(args.cancel).subscribe({
                      next: ({ cancelOrder }) => {
                        if (cancelOrder) {
                          switch (cancelOrder.__typename) {
                            case "Order":
                              this.notiService.success(
                                _("order.cancelled-order-success")
                              );
                              break;
                            case "CancelActiveOrderError":
                            case "QuantityTooGreatError":
                            case "MultipleOrderError":
                            case "OrderStateTransitionError":
                            case "EmptyOrderLineSelectionError":
                            default:
                              this.notiService.error(
                                (cancelOrder as any).message ||
                                  "Order cancellation failed"
                              );
                              break;
                          }
                        } else {
                          this.notiService.error(
                            "No cancel order response received"
                          );
                        }
                      },
                      error: (error) => {
                        this.notiService.error(
                          "Error cancelling order: " + (error.message || error)
                        );
                      },
                    });
                  } else {
                    // Removing console.log statement
                  }
                }
                break;
              case "AlreadyRefundedError":
                this.notiService.error("This order has already been refunded");
                break;
              case "NothingToRefundError":
                this.notiService.error("Nothing to refund for this order");
                break;
              case "PaymentOrderMismatchError":
                this.notiService.error("Payment and order do not match");
                break;
              case "RefundOrderStateError":
                this.notiService.error("Order is not in a refundable state");
                break;
              case "RefundStateTransitionError":
                this.notiService.error("Error transitioning refund state");
                break;
              case "RefundAmountError":
                // Check for specific error details
                const refundAmountErrorMessage =
                  (refundOrder as any).message ||
                  (refundOrder as any).errorMessage ||
                  "Invalid refund amount";

                // Provide user-friendly message for the specific error
                if (
                  refundAmountErrorMessage.includes(
                    "exceeds the refundable amount"
                  )
                ) {
                  this.notiService.error(
                    "The refund amount exceeds what is available for this payment. Please check if partial refunds have already been processed."
                  );
                } else {
                  this.notiService.error(
                    "Refund amount error: " + refundAmountErrorMessage
                  );
                }
                break;
              default:
                this.notiService.error(
                  (refundOrder as any).message ||
                    "Unknown refund error occurred"
                );
                break;
            }
          } else {
            this.notiService.error("No response received from refund service");
          }
        },
        error: (error) => {
          // Extract meaningful error message
          let errorMessage = "Unknown error occurred during refund";
          if (error.message) {
            errorMessage = error.message;
          } else if (error.error && error.error.message) {
            errorMessage = error.error.message;
          } else if (typeof error === "string") {
            errorMessage = error;
          }

          this.notiService.error("Refund failed: " + errorMessage);
        },
      });
    } catch (error) {
      this.notiService.error("Unexpected error occurred during refund");
    }
  }

  setStatus(id: string, status: string) {
    this.dataService
      .mutate(SetRefundRequestStatusDocument, {
        input: {
          id,
          status,
        },
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result.updateRefundRequest.__typename === "RefundRequest") {
          this.notiService.success("Refund Request Updated");
        }
      });
  }
}
